/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Server-paginated table for `{items, total}` endpoints.
 *
 * Not examples/Tables/DataTable: that one paginates and searches client side
 * over the full array, which does not fit the contract's page/page_size lists.
 *
 * columns: [{ key, label, render?: (row) => node, align?, width?, footer?: node }]
 * Footer cells render when any column has `footer` (totals rows).
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Checkbox, Skeleton, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow,
} from '@mui/material';
import MDTypography from 'components/MDTypography';
import { v2 as t } from 'styles/tokens/v2';
import { useIsReV2 } from '../../v2/parts';

const headSx = {
  bgcolor: '#F4F6F9',
  color: '#5C6B82',
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  py: 1.25,
  borderBottom: '1px solid #E8EDF5',
};

/*
 * UI v2: no hard-coded header or cell styles, so theme-v2's table applies:
 * 48px rows, 36px uppercase 12px header, tabular numbers. `dense` does not go
 * below the 48px row in v2.
 */
const V2_SX = {
  head: { whiteSpace: 'nowrap', borderBottom: `1px solid ${t.border}` },
  cell: {},
  footer: { fontWeight: 600, fontSize: 13, color: t.text, bgcolor: t.surface2, borderTop: `1px solid ${t.border2}`, fontVariantNumeric: 'tabular-nums' },
  footerRow: {},
};

const LEGACY_SX = {
  head: headSx,
  footer: { fontWeight: 700, fontSize: '0.8125rem', color: '#0E2348', borderTop: '2px solid #E8EDF5' },
  footerRow: { bgcolor: '#F7F9FC' },
};

export default function ReTable({
  columns, rows, loading, emptyMessage, onRowClick, rowKey,
  total, page, pageSize, onPageChange, onPageSizeChange,
  selectable, selected, onToggle, onToggleAll, isRowSelectable, rowSx, dense,
}) {
  const hasFooter = columns.some((c) => c.footer !== undefined);
  const selectableRows = selectable ? rows.filter((r) => isRowSelectable(r)) : [];
  const allSelected = selectable && selectableRows.length > 0 && selectableRows.every((r) => selected.includes(r[rowKey]));
  const cellPy = dense ? 0.75 : 1.25;
  const isV2 = useIsReV2();
  const sxs = isV2 ? V2_SX : LEGACY_SX;
  const cellSx = isV2 ? V2_SX.cell : { py: cellPy, fontSize: '0.8125rem', color: '#26344A', borderBottom: '1px solid #EEF1F6' };

  return (
    <>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 640 }}>
          {/* The MD theme sets TableHead to display:block; restore table semantics. */}
          <TableHead sx={{ display: 'table-header-group', p: 0 }}>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox" sx={sxs.head}>
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={!allSelected && selected.length > 0}
                    disabled={!selectableRows.length}
                    onChange={() => onToggleAll(allSelected ? [] : selectableRows.map((r) => r[rowKey]))}
                  />
                </TableCell>
              )}
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align || 'left'} sx={{ ...sxs.head, width: c.width }}>
                  {c.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && [0, 1, 2, 3, 4].map((i) => (
              <TableRow key={`sk-${i}`}>
                {selectable && <TableCell padding="checkbox" />}
                {columns.map((c) => (
                  <TableCell key={c.key}><Skeleton variant="text" width="70%" /></TableCell>
                ))}
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length + (selectable ? 1 : 0)} sx={{ py: 4, textAlign: 'center' }}>
                  {isV2
                    ? <MDTypography component="span" sx={{ fontSize: 14, color: t.text3 }}>{emptyMessage}</MDTypography>
                    : <MDTypography variant="button" color="text">{emptyMessage}</MDTypography>}
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.map((row) => {
              const id = row[rowKey];
              const canSelect = selectable && isRowSelectable(row);
              return (
                <TableRow
                  key={id}
                  hover
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={{ cursor: onRowClick ? 'pointer' : 'default', ...(rowSx ? rowSx(row) : {}) }}
                >
                  {selectable && (
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        disabled={!canSelect}
                        checked={selected.includes(id)}
                        onChange={() => onToggle(id)}
                      />
                    </TableCell>
                  )}
                  {columns.map((c) => (
                    <TableCell
                      key={c.key}
                      align={c.align || 'left'}
                      sx={cellSx}
                    >
                      {c.render ? c.render(row) : (row[c.key] ?? '—')}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
          {hasFooter && !loading && rows.length > 0 && (
            <TableFooter>
              <TableRow sx={sxs.footerRow}>
                {selectable && <TableCell />}
                {columns.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.align || 'left'}
                    sx={sxs.footer}
                  >
                    {c.footer ?? ''}
                  </TableCell>
                ))}
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
      {onPageChange && (
        <TablePagination
          component="div"
          count={total || 0}
          page={Math.max(0, (page || 1) - 1)}
          rowsPerPage={pageSize || 25}
          rowsPerPageOptions={[10, 25, 50, 100, 200]}
          onPageChange={(e, p) => onPageChange(p + 1)}
          onRowsPerPageChange={(e) => onPageSizeChange && onPageSizeChange(parseInt(e.target.value, 10))}
        />
      )}
    </>
  );
}

ReTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.node,
    render: PropTypes.func,
    align: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    footer: PropTypes.node,
  })).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  emptyMessage: PropTypes.node,
  onRowClick: PropTypes.func,
  rowKey: PropTypes.string,
  total: PropTypes.number,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  selectable: PropTypes.bool,
  selected: PropTypes.array,
  onToggle: PropTypes.func,
  onToggleAll: PropTypes.func,
  isRowSelectable: PropTypes.func,
  rowSx: PropTypes.func,
  dense: PropTypes.bool,
};

ReTable.defaultProps = {
  rows: [],
  loading: false,
  emptyMessage: 'Nothing to show yet.',
  onRowClick: null,
  rowKey: 'id',
  total: 0,
  page: 1,
  pageSize: 25,
  onPageChange: null,
  onPageSizeChange: null,
  selectable: false,
  selected: [],
  onToggle: () => {},
  onToggleAll: () => {},
  isRowSelectable: () => true,
  rowSx: null,
  dense: false,
};
